import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { DataTable, StatusPill, SortState } from '../patterns';
import { getUnitDiagnostics, UnitDiagnosticRow } from '../../services/consumableInventoryService';
import UnitReviewFixModal from './UnitReviewFixModal';
import { fixModeFor, editPathFor } from './unitReviewFix';

const onHandVariant = (s: UnitDiagnosticRow['onHandStatus']): 'ok' | 'warn' | 'info' =>
  s === 'conflict' ? 'warn' : s === 'ok' ? 'ok' : 'info';

const UnitReviewTable: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<UnitDiagnosticRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState<SortState>({ key: 'product', direction: 'asc' });
  const [fixing, setFixing] = useState<UnitDiagnosticRow | null>(null);

  const load = () => {
    setLoading(true);
    setError('');
    getUnitDiagnostics()
      .then((data) => { setRows(data); setLoading(false); })
      .catch((err: { message?: string }) => { setError(err?.message ?? t('common.error', { defaultValue: 'Failed to load' })); setLoading(false); });
  };

  useEffect(() => {
    load();
    // load() reads t() only on the error branch; intentionally excluded from
    // deps so a changing t identity doesn't retrigger the fetch (matches the
    // pattern used by StockLevelsTable / ConsumableStockLevelsPage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reasonLabel = (code: string): string =>
    t(`inventory.unitConflict.reason.${code}`, { defaultValue: code });

  // Shared Fix control — a modal-opening button for ledger-row conflicts, or a
  // deep-link to the product form for config conflicts. Used in both layouts.
  const FixControl: React.FC<{ row: UnitDiagnosticRow }> = ({ row }) =>
    fixModeFor(row) === 'modal' ? (
      <button type="button" className="pat-btn pat-btn--subtle" onClick={() => setFixing(row)}>
        {t('consumables.unitReview.fix')}
      </button>
    ) : (
      <Link className="pat-btn pat-btn--subtle" to={editPathFor(row.consumableType, row.productId)}>
        {t('consumables.unitReview.fix')}
      </Link>
    );

  const sortedRows = [...rows].sort((a, b) => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = sort.key === 'type' ? 'consumableType' : 'productName';
    return String(a[key as keyof UnitDiagnosticRow] ?? '').localeCompare(
      String(b[key as keyof UnitDiagnosticRow] ?? ''),
      undefined,
      { numeric: true, sensitivity: 'base' },
    ) * dir;
  });

  if (loading) return <div>{t('common.loading', { defaultValue: 'Loading…' })}</div>;
  if (error) {
    return (
      <Alert variant="danger" role="alert">
        <div>{error}</div>
        <button type="button" className="pat-btn pat-btn--subtle" onClick={load}>
          {t('common.retry', { defaultValue: 'Retry' })}
        </button>
      </Alert>
    );
  }

  return (
    <div data-testid="unit-review-table">
      <DataTable<UnitDiagnosticRow>
        rows={sortedRows}
        rowKey={(r) => r.productId}
        sort={sort}
        onSortChange={setSort}
        emptyState={t('consumables.unitReview.empty')}
        columns={[
          {
            key: 'product',
            label: t('consumables.unitReview.col.product'),
            sortable: true,
            render: (r) => r.productName,
          },
          {
            key: 'type',
            label: t('consumables.unitReview.col.type'),
            sortable: true,
            render: (r) => r.consumableType,
          },
          {
            key: 'stockUnit',
            label: t('consumables.unitReview.col.stockUnit'),
            render: (r) => r.stockUnit ?? '—',
          },
          {
            key: 'doseUnit',
            label: t('consumables.unitReview.col.doseUnit'),
            render: (r) => r.doseUnit ?? '—',
          },
          {
            key: 'reason',
            label: t('consumables.unitReview.col.reason'),
            render: (r) => r.reasons.map(reasonLabel).join('; '),
          },
          {
            key: 'onHand',
            label: t('consumables.unitReview.col.onHand'),
            render: (r) => (
              <StatusPill variant={onHandVariant(r.onHandStatus)}>
                {t(`consumables.unitReview.onHand.${r.onHandStatus}`)}
              </StatusPill>
            ),
          },
          {
            key: 'fix',
            label: '',
            render: (r) => <FixControl row={r} />,
          },
        ]}
        mobileCardRow={(r) => (
          <div>
            <strong>{`${r.productName} (${r.consumableType})`}</strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {`${t('consumables.unitReview.col.stockUnit')}: ${r.stockUnit ?? '—'} · ${t('consumables.unitReview.col.doseUnit')}: ${r.doseUnit ?? '—'}`}
            </div>
            <div style={{ fontSize: 11 }}>{r.reasons.map(reasonLabel).join('; ')}</div>
            <FixControl row={r} />
          </div>
        )}
      />
      {fixing && (
        <UnitReviewFixModal
          productId={fixing.productId}
          productName={fixing.productName}
          onClose={() => setFixing(null)}
          onResolved={load}
        />
      )}
    </div>
  );
};

export default UnitReviewTable;
